/**
 * Один источник состояния дуэли на всё приложение.
 *
 * Экран дуэлей и полоска HUD над меню показывают одно и то же, но живут в
 * разных местах дерева. Если бы каждый опрашивал сервер сам, во время матча
 * запросы шли бы вдвое чаще нужного — а во время матча их и так много.
 *
 * Частота опроса зависит от того, что происходит: идёт матч — раз в 3 секунды
 * (счёт должен шевелиться), ищем соперника — раз в 4, всё спокойно — раз в 25.
 */

import api from '../api/client';

let state = null;
let lastError = null;
let timer = null;
let inflight = null;
const subscribers = new Set();

const IDLE_MS = 25000;
const SEARCH_MS = 4000;
const ACTIVE_MS = 3000;

function interval() {
  if (state?.active) return ACTIVE_MS;
  if (state?.searching) return SEARCH_MS;
  if (state?.incoming?.length || state?.unseen_results?.length) return 8000;
  return IDLE_MS;
}

function emit() {
  subscribers.forEach((fn) => {
    try {
      fn(state, lastError);
    } catch (_) {
      /* подписчик не должен ронять цикл опроса */
    }
  });
}

export async function refreshDuel({ silent = true } = {}) {
  if (inflight) return inflight;
  inflight = api
    .duelState()
    .then((data) => {
      state = data;
      lastError = null;
      emit();
      return data;
    })
    .catch((err) => {
      lastError = err;
      // Прошлое состояние оставляем: пустая арена посреди матча пугает
      // сильнее, чем слегка устаревший счёт.
      if (!silent) emit();
      return null;
    })
    .finally(() => {
      inflight = null;
      schedule();
    });
  return inflight;
}

const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

function schedule() {
  if (timer) clearTimeout(timer);
  timer = null;
  if (!subscribers.size) return;
  // Свёрнутое приложение опрашивать нечем и незачем: HUD дуэли никто не видит,
  // а запрос раз в 25 секунд будит радио телефона круглые сутки — это заметная
  // часть нагрева и расхода батареи. Возвращаемся — сразу дозапрашиваем.
  if (hidden()) return;
  timer = setTimeout(() => refreshDuel(), interval());
}

function onVisibility() {
  if (hidden()) {
    if (timer) clearTimeout(timer);
    timer = null;
    return;
  }
  if (subscribers.size) refreshDuel();
}

export function subscribeDuel(fn) {
  subscribers.add(fn);
  if (state) fn(state, lastError);
  if (subscribers.size === 1) {
    document.addEventListener('visibilitychange', onVisibility);
    refreshDuel();
  } else {
    schedule();
  }
  return () => {
    subscribers.delete(fn);
    if (!subscribers.size) {
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer) clearTimeout(timer);
      timer = null;
    }
  };
}

export function getDuelState() {
  return state;
}

/** Подставить свежий ответ после действия, не дожидаясь следующего опроса. */
export function pushDuelState(next) {
  if (!next) return;
  state = next;
  emit();
  schedule();
}

/** Локальная правка: например, спрятать итог, который игрок уже закрыл. */
export function patchDuelState(patch) {
  if (!state) return;
  state = { ...state, ...patch };
  emit();
}
